import { useState } from 'react'
import { Check, ChevronRight, Loader2, X } from 'lucide-react'
import { Badge } from '@/components/ui/badge'

export interface ToolCallView {
  id: string
  runId: string | null
  toolName: string
  status: 'running' | 'completed' | 'failed'
  result?: unknown
  error?: string
}

/** Turns list_inquiries into "Inquiries", which is what an operator recognises. */
function toolLabel(name: string): string {
  const label = name
    .replace(/^(list|get|search)_/, '')
    .replace(/_/g, ' ')
    .trim()
  return label.charAt(0).toUpperCase() + label.slice(1)
}

function verb(name: string): string {
  if (name.startsWith('search')) return 'Searched'
  if (name.startsWith('get')) return 'Read'
  if (name.startsWith('list')) return 'Listed'
  return 'Ran'
}

function resultCount(result: unknown): number | null {
  if (Array.isArray(result)) return result.length
  return null
}

/**
 * A compact record of one tool call, collapsed by default.
 *
 * Operators mostly want to know the agent looked, not what it saw - so the
 * detail is available but not in the way, like the "Worked for 14 seconds"
 * disclosure in a Linear thread.
 */
export function ToolCallCard({ call }: { call: ToolCallView }) {
  const [open, setOpen] = useState(false)
  const count = resultCount(call.result)

  return (
    <div className="min-w-0 rounded-md border border-border/60 bg-muted/30 text-sm">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-muted/50"
        aria-expanded={open}
      >
        {call.status === 'running' ? (
          <Loader2 className="size-3.5 shrink-0 animate-spin text-muted-foreground" />
        ) : call.status === 'failed' ? (
          <X className="size-3.5 shrink-0 text-destructive" />
        ) : (
          <Check className="size-3.5 shrink-0 text-done" />
        )}

        <span className="text-muted-foreground">
          {verb(call.toolName)} <span className="text-foreground">{toolLabel(call.toolName)}</span>
        </span>

        {count !== null && (
          <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs tabular-nums">
            {count}
          </Badge>
        )}

        <ChevronRight
          className={`ml-auto size-3.5 shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-90' : ''}`}
        />
      </button>

      {open && (
        <div className="min-w-0 border-t border-border/60 px-3 py-2">
          {call.error ? (
            <p className="text-destructive">{call.error}</p>
          ) : (
            <pre className="max-h-64 max-w-full overflow-auto whitespace-pre text-xs text-muted-foreground">
              {JSON.stringify(call.result ?? null, null, 2)}
            </pre>
          )}
        </div>
      )}
    </div>
  )
}
